import React from "react";
import { ContactButton } from "./ContactButton";
import clsx from "clsx";

interface SectionProps {
	children: React.ReactNode;
	variant?: "white" | "surface" | "tint" | "dark";
	className?: string;
}

export function Section({
	children,
	variant = "white",
	className = "",
}: SectionProps) {
	const bg = {
		white: "tw-bg-white",
		surface: "tw-bg-surface tw-border-y tw-border-line",
		tint: "tw-bg-tint",
		dark: "tw-bg-red tw-text-white",
	}[variant];

	return (
		<section className={`${bg} ${className}`}>
			<div className="container">{children}</div>
		</section>
	);
}

export function SectionHead({
	title,
	intro,
	center = false,
}: {
	title: string;
	intro?: string;
	center?: boolean;
}) {
	return (
		<div className={clsx("section-title", { "tw-text-center": center })}>
			<h2 className="h3">{title}</h2>
			{intro && (
				<p
					className={`tw-max-w-4xl tw-text-[17px] tw-font-light ${center ? "tw-mx-auto" : ""}`}
				>
					{intro}
				</p>
			)}
		</div>
	);
}

// Card used for category tiles, audience tiles, benefit tiles, etc.
export function Card({
	title,
	body,
	pill,
	feeds,
	icon,
}: {
	title: string;
	body: string;
	pill?: string;
	feeds?: string;
	icon?: React.ReactNode;
}) {
	return (
		<div className="card tw-flex tw-h-full tw-flex-col">
			{icon && (
				<div className="icon tw-flex tw-items-center tw-justify-center tw-rounded-card tw-bg-tint tw-text-red">
					{icon}
				</div>
			)}
			<h3 className="h5">{title}</h3>
			<p className="tw-text-[14px] tw-leading-relaxed tw-text-body">
				{body}
			</p>
			{pill && (
				<span className="pill tw-inline-block tw-w-fit tw-rounded tw-border tw-border-line tw-bg-surface tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-muted">
					{pill}
				</span>
			)}
			{feeds && (
				<div className="badge tw-text-[11.5px] tw-text-muted">
					{feeds}
				</div>
			)}
		</div>
	);
}

// The recurring red call-to-action band. The button opens the Freshdesk form.
export function CtaBand({
	title,
	body,
	button,
}: {
	title: string;
	body: string;
	button: string;
}) {
	return (
		<section className="tw-bg-red tw-text-white">
			<div className="container tw-flex tw-flex-col tw-items-start tw-justify-between tw-gap-6 md:tw-flex-row md:tw-items-center">
				<div>
					<h2
						className="h4 tw-text-white"
						style={{ fontWeight: 300 }}
					>
						{title}
					</h2>
					<p className="tw-font-light tw-text-white/90">{body}</p>
				</div>
				<ContactButton className="btn btn--white">
					{button} &rarr;
				</ContactButton>
			</div>
		</section>
	);
}
